import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { 
  ChevronLeft, 
  MapPin,
  Navigation,
  Search,
  CheckCircle,
  Loader,
  Phone,
  User as UserIcon,
  Clock,
  AlertCircle
} from 'lucide-react';
import { RecommendedProductsRow } from './RecommendedProductsRow'; // ← NEW

const API_URL = process.env.REACT_APP_API_URL;
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

function ControlTower({ delivery, onBack }) {
  const hasLocation = delivery.latitude && delivery.longitude;

  return hasLocation ? (
    <DriverTracking delivery={delivery} onBack={onBack} />
  ) : (
    <LocationConfirmation delivery={delivery} onBack={onBack} />
  );
}

// ─── LocationConfirmation: no ads here — user is focused on a task ────────────
function LocationConfirmation({ delivery, onBack }) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const geocoderRef = useRef(null);
  const autocompleteServiceRef = useRef(null);

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [locationMethod, setLocationMethod] = useState(null);

  useEffect(() => {
    if (!map.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [-77.0428, -12.0464],
        zoom: 12,
      });
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
      map.current.on('click', (e) => handleMapClick(e.lngLat));
    }
    loadGoogleMapsScript();
    return () => { if (marker.current) marker.current.remove(); };
  }, []);

  const loadGoogleMapsScript = () => {
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        geocoderRef.current = new window.google.maps.Geocoder();
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      };
      document.head.appendChild(script);
    } else {
      geocoderRef.current = new window.google.maps.Geocoder();
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    }
  };

  const handleMapClick = (lngLat) => {
    placeMarker(lngLat.lat, lngLat.lng);
    setLocationMethod('tap');
  };

  const handleSearchInput = (value) => {
    setSearchQuery(value);
    if (value.length > 2 && autocompleteServiceRef.current) {
      autocompleteServiceRef.current.getPlacePredictions(
        { input: value, componentRestrictions: { country: 'pe' } },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions);
          } else {
            setSuggestions([]);
          }
        }
      );
    } else {
      setSuggestions([]);
    }
  };

  const handleSelectSuggestion = (placeId) => {
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ placeId }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();
        placeMarker(lat, lng);
        map.current.flyTo({ center: [lng, lat], zoom: 16 });
        setSuggestions([]);
        setSearchQuery(results[0].formatted_address);
        setLocationMethod('search');
      }
    });
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Tu navegador no soporta geolocalización');
      return;
    }
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        placeMarker(lat, lng);
        map.current.flyTo({ center: [lng, lat], zoom: 16 });
        setLocationMethod('gps');
      },
      () => {
        setError('No se pudo obtener tu ubicación. Por favor, permite el acceso a tu ubicación.');
      }
    );
  };

  const placeMarker = (lat, lng) => {
    if (marker.current) marker.current.remove();
    marker.current = new mapboxgl.Marker({ color: '#B1336E' })
      .setLngLat([lng, lat])
      .addTo(map.current);
    setSelectedLocation({ latitude: lat, longitude: lng });
    setError(null);
  };

  const handleSaveLocation = async () => {
    if (!selectedLocation) {
      setError('Por favor, selecciona una ubicación primero');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await axios.post(`${API_URL}/api/delivery/${delivery.id}/location`, {
        latitude: selectedLocation.latitude,
        longitude: selectedLocation.longitude,
      });
      onBack();
    } catch {
      setError('No se pudo guardar la ubicación. Por favor, intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-white">
      <div className="flex-shrink-0 bg-gradient-to-r from-brand-primary to-pink-600 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-colors duration-200 active:scale-95">
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex items-center gap-3">
              <div className="bg-white p-2 rounded-lg shadow-md">
                <MapPin className="w-6 h-6 text-brand-primary" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-extrabold text-white font-montserrat">Confirmar Ubicación</h1>
                <p className="text-xs text-white/90 font-montserrat">Ayúdanos a encontrarte</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 bg-blue-50 border-b border-blue-200 px-4 py-3">
        <div className="max-w-4xl mx-auto">
          <div className="flex gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-900 font-montserrat">
              Selecciona tu ubicación exacta para que el conductor pueda llegar sin problemas
            </p>
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-4xl mx-auto relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchInput(e.target.value)}
              placeholder="Buscar dirección..."
              className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl font-montserrat text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>
          {suggestions.length > 0 && (
            <div className="absolute z-10 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.place_id}
                  onClick={() => handleSelectSuggestion(suggestion.place_id)}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors"
                >
                  <div className="flex gap-2">
                    <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
                    <span className="text-sm text-slate-900 font-montserrat">{suggestion.description}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 relative">
        <div ref={mapContainer} className="w-full h-full" />
        <button
          onClick={handleGetCurrentLocation}
          className="absolute bottom-24 right-4 p-3 bg-white rounded-full shadow-lg border border-slate-200 hover:bg-slate-50 active:scale-95 transition-all"
        >
          <Navigation className="w-6 h-6 text-brand-primary" />
        </button>
      </div>

      <div className="flex-shrink-0 bg-white border-t border-slate-200 px-4 py-4 safe-area-bottom">
        <div className="max-w-4xl mx-auto">
          {error && (
            <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800 font-montserrat">{error}</p>
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={handleSaveLocation}
              disabled={!selectedLocation || saving}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold font-montserrat transition-all ${
                selectedLocation && !saving
                  ? 'bg-brand-green text-white hover:bg-brand-green/90 active:scale-95'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              {saving ? (
                <><Loader className="w-5 h-5 animate-spin" /><span>Guardando...</span></>
              ) : (
                <><CheckCircle className="w-5 h-5" /><span>Confirmar Ubicación</span></>
              )}
            </button>
          </div>
          {selectedLocation && (
            <p className="text-xs text-center text-slate-600 font-montserrat mt-2">
              Ubicación seleccionada mediante {locationMethod === 'tap' ? 'toque en mapa' : locationMethod === 'search' ? 'búsqueda' : 'GPS'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Marker animation styles ──────────────────────────────────────────────────
const MARKER_STYLES = `
  .driver-marker-container { width:50px;height:50px;display:flex;align-items:center;justify-content:center;position:relative;z-index:2;animation:pulse-scale 1.5s ease-in-out infinite; }
  .driver-marker-logo { width:100%;height:100%;object-fit:contain;filter:drop-shadow(0 3px 10px rgba(0,0,0,.4)); }
  @keyframes pulse-scale { 0%,100%{transform:scale(1)}50%{transform:scale(1.2)} }
  .driver-marker-glow { position:absolute;width:70px;height:70px;top:50%;left:50%;transform:translate(-50%,-50%);border-radius:50%;background:radial-gradient(circle,rgba(103,164,60,.5) 0%,transparent 70%);animation:glow-pulse 2s ease-in-out infinite;pointer-events:none;z-index:1; }
  @keyframes glow-pulse { 0%,100%{transform:translate(-50%,-50%) scale(1);opacity:.4}50%{transform:translate(-50%,-50%) scale(1.4);opacity:.7} }
`;

// ─── DriverTracking: ads appear below the arrival time strip ─────────────────
function DriverTracking({ delivery, onBack }) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const clientMarker = useRef(null);
  const driverMarker = useRef(null);
  const isFirstLoad = useRef(true);
  const stylesAdded = useRef(false);

  const [driverData, setDriverData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [arrivalTimeRange, setArrivalTimeRange] = useState(null);

  const addMarkerStyles = () => {
    if (!stylesAdded.current && !document.getElementById('driver-marker-styles')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'driver-marker-styles';
      styleElement.innerHTML = MARKER_STYLES;
      document.head.appendChild(styleElement);
      stylesAdded.current = true;
    }
  };

  useEffect(() => {
    if (map.current) return;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [delivery.longitude, delivery.latitude],
      zoom: 14,
    });
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    clientMarker.current = new mapboxgl.Marker({ color: '#B1336E' })
      .setLngLat([delivery.longitude, delivery.latitude])
      .setPopup(new mapboxgl.Popup({ offset: 25, closeButton: false }).setHTML('<p class="font-semibold">Tu ubicación</p>'))
      .addTo(map.current);
    if (delivery.arrival_time) calculateArrivalTimeRange(delivery.arrival_time);
    addMarkerStyles();
    return () => {
      if (map.current) { map.current.remove(); map.current = null; }
    };
  }, []);

  useEffect(() => {
    if (!map.current) return;
    fetchDriverLocation();
    const interval = setInterval(fetchDriverLocation, 5000);
    return () => clearInterval(interval);
  }, [delivery.driver_id]);

  const calculateArrivalTimeRange = (arrivalTimeStr) => {
    const arrivalTime = new Date(arrivalTimeStr);
    const start = new Date(arrivalTime.getTime() - 15 * 60000);
    const end = new Date(arrivalTime.getTime() + 105 * 60000);
    setArrivalTimeRange({
      start: start.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }),
      end: end.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }),
    });
  };

  const fetchDriverLocation = async () => {
    if (!delivery.driver_id) {
      setError('No hay conductor asignado todavía');
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/location/driver`, {
        params: { driver_id: delivery.driver_id }
      });
      setDriverData(response.data);
      setError(null);
      if (response.data.latitude && response.data.longitude) {
        const driverLngLat = [response.data.longitude, response.data.latitude];
        if (driverMarker.current) {
          driverMarker.current.setLngLat(driverLngLat);
        } else {
          const markerElement = document.createElement('div');
          markerElement.style.cssText = 'position:relative;width:50px;height:50px;cursor:pointer;';
          const glowElement = document.createElement('div');
          glowElement.className = 'driver-marker-glow';
          markerElement.appendChild(glowElement);
          const logoContainer = document.createElement('div');
          logoContainer.className = 'driver-marker-container';
          const logoImg = document.createElement('img');
          logoImg.src = '/logo.png';
          logoImg.alt = 'Driver';
          logoImg.className = 'driver-marker-logo';
          logoImg.onerror = () => {
            logoImg.style.display = 'none';
            const fallback = document.createElement('div');
            fallback.style.cssText = 'width:40px;height:40px;background:#67A43C;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;font-size:20px;';
            fallback.textContent = '🚗';
            logoContainer.appendChild(fallback);
          };
          logoContainer.appendChild(logoImg);
          markerElement.appendChild(logoContainer);
          driverMarker.current = new mapboxgl.Marker({ element: markerElement, anchor: 'center' })
            .setLngLat(driverLngLat)
            .setPopup(new mapboxgl.Popup({ offset: 30, closeButton: false }).setHTML(`
              <div style="padding:8px;font-family:Montserrat,sans-serif;">
                <p style="margin:0;font-weight:600;color:#67A43C;">🚗 ${response.data.name}</p>
                <p style="margin:4px 0 0;font-size:12px;color:#64748b;">En camino</p>
              </div>
            `))
            .addTo(map.current);
        }
        if (isFirstLoad.current && map.current) {
          const bounds = new mapboxgl.LngLatBounds();
          bounds.extend([delivery.longitude, delivery.latitude]);
          bounds.extend(driverLngLat);
          map.current.fitBounds(bounds, { padding: { top: 200, bottom: 250, left: 50, right: 50 }, maxZoom: 18 });
          isFirstLoad.current = false;
        }
      }
    } catch {
      setError('No se pudo obtener la ubicación del conductor');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppDriver = () => {
    if (driverData?.phone_number) {
      const message = encodeURIComponent(`Hola, soy ${delivery.recipient_name}. Estoy esperando mi entrega.`);
      window.open(`https://wa.me/${driverData.phone_number.replace(/\D/g, '')}?text=${message}`, '_blank');
    }
  };

  const handleRecenterMap = () => {
    if (map.current && driverMarker.current && clientMarker.current) {
      const bounds = new mapboxgl.LngLatBounds();
      bounds.extend([delivery.longitude, delivery.latitude]);
      bounds.extend(driverMarker.current.getLngLat());
      map.current.fitBounds(bounds, { padding: { top: 200, bottom: 250, left: 50, right: 50 }, maxZoom: 18, duration: 1000 });
    }
  };

  return (
    <div className="h-screen flex flex-col bg-white">
      {/* Header */}
      <div className="flex-shrink-0 bg-gradient-to-r from-brand-green to-brand-gold shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-colors duration-200 active:scale-95">
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex items-center gap-3">
              <div className="bg-white p-2 rounded-lg shadow-md">
                <Navigation className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-extrabold text-white font-montserrat">Seguir Conductor</h1>
                <p className="text-xs text-white/90 font-montserrat">Tu pedido va en camino</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Driver Info */}
      {driverData && (
        <div className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-3">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-green to-brand-gold flex items-center justify-center flex-shrink-0">
                  <UserIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900 font-montserrat">{driverData.name}</p>
                  <p className="text-xs text-slate-600 font-montserrat">Tu conductor</p>
                </div>
              </div>
              <button
                onClick={handleWhatsAppDriver}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 active:scale-95 transition-all font-montserrat font-semibold text-sm"
              >
                <Phone className="w-4 h-4" />
                <span>WhatsApp</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Map — flex-1 so it fills the available space */}
      <div className="flex-1 relative">
        {loading && !driverData && (
          <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
            <div className="text-center">
              <Loader className="w-8 h-8 text-brand-primary animate-spin mx-auto mb-2" />
              <p className="text-sm text-slate-600 font-montserrat">Buscando conductor...</p>
            </div>
          </div>
        )}
        {error && !driverData && (
          <div className="absolute inset-0 bg-white z-10 flex items-center justify-center p-4">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-orange-500 mx-auto mb-3" />
              <p className="text-slate-900 font-semibold font-montserrat mb-1">Conductor no disponible</p>
              <p className="text-sm text-slate-600 font-montserrat">{error}</p>
            </div>
          </div>
        )}
        <div ref={mapContainer} className="w-full h-full" />
        {driverMarker.current && (
          <button
            onClick={handleRecenterMap}
            className="absolute bottom-4 right-4 p-3 bg-white rounded-full shadow-lg border border-slate-200 hover:bg-slate-50 active:scale-95 transition-all z-20"
          >
            <Navigation className="w-6 h-6 text-brand-primary" />
          </button>
        )}
      </div>

      {/* Arrival Time */}
      {arrivalTimeRange && (
        <div className="flex-shrink-0 bg-gradient-to-r from-brand-primary/10 to-brand-gold/10 border-t border-brand-primary/20 px-4 py-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-brand-primary flex-shrink-0" />
              <div>
                <p className="text-xs text-slate-600 font-montserrat">Hora estimada de llegada</p>
                <p className="text-lg font-bold text-brand-primary font-montserrat">
                  {arrivalTimeRange.start} - {arrivalTimeRange.end}
                </p>
                <p className="text-xs text-slate-500 font-montserrat mt-1">
                  * El tiempo puede variar según el tráfico
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── ADS: Compact strip below arrival time ── */}
      {/* Uses compact=true — minimal padding, small text, fits the tight footer area */}
      {/* The component self-hides if dismissed or no ads available */}
      <div className="flex-shrink-0 bg-white border-t border-slate-100 safe-area-bottom">
        <RecommendedProductsRow
          deliveryId={delivery.id}
          placement="control_tower"
          compact={true}
        />
      </div>
    </div>
  );
}

export default ControlTower;
